import './draggablePlugin.less';

import React from 'react';
import { makeAutoObservable } from 'mobx';
import { HolderOutlined } from '@ant-design/icons';

import { GridDataEntry } from 'controls/grid/gridDataEntry';
import { GridPlugin, GridSelectionType } from 'controls/grid/gridConfiguration';
import { GridStore } from 'controls/grid/gridStore';
import { MobxManager } from 'framework/mobx-integration';
import { GridColumnConfig } from 'controls/grid/gridColumnConfig';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { EntityType } from 'controls/designer/features/ceeview-entity-drop/types';
import { CeeviewEntityDragAndDropStore } from 'framework/ceeviewEntityDragAndDropStore';
import { EntityDescription } from 'framework/EntityDescription';
import translator from 'core/localization/localization';
import b_ from 'b_';

const i18n = translator({
	'Drag handle': {
		no: 'Drahåndtak',
	},
});

type DraggablePluginConfig<DataItem extends GridDataEntry> = {
	entityType: EntityType;
	attributes?: (keyof DataItem)[];
	idField?: keyof DataItem;
};

const b = b_.with('grid-draggable');

export class DraggablePlugin<DataItem extends GridDataEntry> implements GridPlugin<DataItem> {
	static ID: string = 'draggable';

	gridStore: GridStore<DataItem>;

	disabled: boolean;
	disabledReason: string;

	mobx = new MobxManager();

	constructor(public config: DraggablePluginConfig<DataItem>) {
		makeAutoObservable(this);
	}

	get id() {
		return DraggablePlugin.ID;
	}

	columnsFilter = (columns: GridColumnConfig<DataItem>[]) => {
		columns = [...columns];

		// right after the selection checkboxes, or first when the grid has none
		const index = this.gridStore?.config.selection === GridSelectionType.None ? 0 : 1;
		columns.splice(index, 0, {
			field: 'draggable',
			title: i18n('Drag handle'),
			renderer: (item, extra) => (
				<HolderOutlined
					className={b('handle')}
					onDragStart={(e) => this.dragStart(e, extra.gridItem)}
					onDragEnd={this.dragEnd}
					draggable={true}
				/>
			),
			rendererHeader: () => '',
			selectable: false,
			width: 32,
		});

		return columns;
	};

	dragStart = (e: React.DragEvent, item: GridDataItem<DataItem>) => {
		CeeviewEntityDragAndDropStore.startDragging(e, this.getDataSourceDescription(item));
	};

	getDataSourceDescription(item: GridDataItem<DataItem>) {
		const dataSource = {
			type: this.config.entityType,
			id: this.config.idField ? item.data[this.config.idField] : item.id,
		} as Record<string, unknown>;

		if ('accountId' in item.data) {
			dataSource.accountId = item.data.accountId;
		}

		this.config.attributes?.forEach((x) => {
			dataSource[x as string] = item.data[x];
		});

		return dataSource as EntityDescription;
	}

	getSelection() {
		return this.gridStore.selection.dataset.items.map((item) => this.getDataSourceDescription(item));
	}

	dragEnd = () => {
		CeeviewEntityDragAndDropStore.stopDragging();
	};

	async attach(store: GridStore<DataItem>) {
		this.gridStore = store;
		return Promise.resolve();
	}

	destroy() {
		this.mobx.destroy();
	}
}
